import React, { useState } from 'react';

/**
 * ReadMoreText - Component for displaying long text with "Read more/Read less" functionality
 * Supports basic markdown formatting
 * @param {string} text - The text to display (can be markdown)
 * @param {number} maxLength - Maximum length before truncating (default: 500)
 * @param {string} className - Additional CSS classes
 */
export default function ReadMoreText({ text, maxLength = 500, className = '' }) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Check if text is empty or null/undefined
  if (!text || (typeof text === 'string' && text.trim().length === 0)) {
    return (
      <div className={`text-gray-500 italic ${className}`}>
        No content available
      </div>
    );
  }

  // Ensure text is a string
  const textStr = String(text);

  const shouldTruncate = textStr.length > maxLength;
  let displayText = isExpanded || !shouldTruncate ? textStr : `${textStr.substring(0, maxLength)}...`;

  // Basic markdown parsing for better readability
  const formatMarkdown = (text) => {
    // Split by lines to process markdown
    const lines = text.split('\n');
    const elements = [];
    let inList = false;
    let listItems = [];
    
    const processInlineMarkdown = (line) => {
      let processed = line;
      // Bold text **text**
      processed = processed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      // Italic text *text* (but not if it's part of **)
      processed = processed.replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
      // Code `code`
      processed = processed.replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">$1</code>');
      return processed;
    };
    
    lines.forEach((line, index) => {
      const trimmed = line.trim();
      
      // Headers
      if (trimmed.startsWith('### ')) {
        if (inList) {
          elements.push(<ul key={`ul-${index}`} className="list-disc list-inside mb-4 space-y-1 ml-4">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        elements.push(
          <h3 key={`h3-${index}`} className="text-lg font-semibold mb-2 mt-4 text-gray-900">
            {trimmed.replace('### ', '')}
          </h3>
        );
      } else if (trimmed.startsWith('## ')) {
        if (inList) {
          elements.push(<ul key={`ul-${index}`} className="list-disc list-inside mb-4 space-y-1 ml-4">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        elements.push(
          <h2 key={`h2-${index}`} className="text-xl font-semibold mb-3 mt-5 text-gray-900">
            {trimmed.replace('## ', '')}
          </h2>
        );
      } else if (trimmed.startsWith('# ')) {
        if (inList) {
          elements.push(<ul key={`ul-${index}`} className="list-disc list-inside mb-4 space-y-1 ml-4">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        elements.push(
          <h1 key={`h1-${index}`} className="text-2xl font-bold mb-4 mt-6 text-gray-900">
            {trimmed.replace('# ', '')}
          </h1>
        );
      } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        // List items
        if (!inList) {
          inList = true;
        }
        const listContent = processInlineMarkdown(trimmed.substring(2));
        listItems.push(
          <li key={`li-${index}`} className="mb-1" dangerouslySetInnerHTML={{ __html: listContent }} />
        );
      } else if (trimmed === '') {
        // Empty line
        if (inList) {
          elements.push(<ul key={`ul-${index}`} className="list-disc list-inside mb-4 space-y-1 ml-4">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        elements.push(<br key={`br-${index}`} />);
      } else {
        // Regular paragraph
        if (inList) {
          elements.push(<ul key={`ul-${index}`} className="list-disc list-inside mb-4 space-y-1 ml-4">{listItems}</ul>);
          listItems = [];
          inList = false;
        }
        const processedLine = processInlineMarkdown(trimmed);
        elements.push(
          <p 
            key={`p-${index}`} 
            className="mb-4 text-base leading-7 text-gray-800"
            dangerouslySetInnerHTML={{ __html: processedLine }}
          />
        );
      }
    });
    
    // Close any remaining list
    if (inList && listItems.length > 0) {
      elements.push(<ul key="ul-final" className="list-disc list-inside mb-4 space-y-1 ml-4">{listItems}</ul>);
    }
    
    return elements;
  };

  return (
    <div className={className}>
      <div className="prose prose-sm max-w-none text-gray-800 leading-relaxed break-words">
        {formatMarkdown(displayText)}
      </div>
      {shouldTruncate && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
        >
          {isExpanded ? (
            <>
              <span>Read less</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </>
          ) : (
            <>
              <span>Read more</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </>
          )}
        </button>
      )}
    </div>
  );
}

